import mapboxgl from 'mapbox-gl';
import proj4 from 'proj4';
import { firstValueFrom } from 'rxjs';
import type { MeshPhongMaterial } from 'three';
import { Site } from 'src/app/bo/site';
import { HoleService } from 'src/app/services/hole.service';
import { HttpService, MAX_TERRAINS_PER_REQUEST } from 'src/app/services/http.service';
import { TerrainLoaderService } from 'src/app/services/terrain-loader.service';

const HOLES_LAYER_ID = 'blast-holes-3d';
const SITE_TERRAIN_LAYER_ID = 'laz-terrain-mesh';
const TERRAIN_EXAGGERATION = 1.8;
const TERRAIN_OPACITY = 1;
const TERRAIN_HEADER_SIZE = 8;
/** Au-delà (unités de l'EPSG du site), un trou est une donnée aberrante. */
const MAX_DISTANCE_FROM_SITE = 50000;
const FALLBACK_CENTER: [number, number] = [6.8649, 45.8326];

export interface Map3dDeps {
  site: Site;
  token: string;
  holeService: HoleService;
  http: HttpService;
  terrainLoader: TerrainLoaderService;
  onLoadingChange: (loading: boolean) => void;
}

/**
 * Vue 3D de la carte du site : Mapbox GL sur fond neutre, avec les trous des tirs
 * visibles, leurs terrains et le terrain du site rendus par Three.js dans des
 * custom layers. Reprend le rendu de l'ancienne carte.
 */
export class Map3dView {
  private map: mapboxgl.Map | null = null;
  private three: any = null;
  private scene: any = null;
  private ref: { x: number; y: number; z: number } | null = null;
  private shared: { top: any; bottom: any; line: any; sphere: any } | null = null;
  private readonly holesCache = new Map<number, any[]>();
  private readonly terrainCache = new Map<number, ArrayBuffer>();
  private readonly holeMeshes = new Map<number, any[]>();
  private readonly terrainGroups = new Map<number, any>();
  private lastKey = '';
  private generation = 0;
  private destroyed = false;
  private pendingIds: number[] = [];
  private siteTerrainRequested = false;
  private loaded = false;

  constructor(private readonly deps: Map3dDeps) {}

  get ready(): boolean {
    return this.loaded;
  }

  async start(container: HTMLElement, center: [number, number] | null, zoom: number, blastIds: number[]): Promise<void> {
    this.pendingIds = blastIds;
    (mapboxgl as any).workerUrl = 'https://api.mapbox.com/mapbox-gl-js/v3.18.1/mapbox-gl-csp-worker.js';
    (mapboxgl as any).accessToken = this.deps.token;

    const hasCenter = !!center && (Math.abs(center[0]) >= 0.1 || Math.abs(center[1]) >= 0.1);
    const map = new mapboxgl.Map({
      container,
      style: { version: 8, sources: {}, layers: [{ id: 'bg', type: 'background', paint: { 'background-color': '#4a6fa5' } }] },
      center: hasCenter ? center! : FALLBACK_CENTER,
      zoom: Math.max(zoom, 13),
      pitch: 75,
      bearing: 0,
      antialias: true,
      dragRotate: true,
      dragPan: true,
      preserveDrawingBuffer: true,
      failIfMajorPerformanceCaveat: false,
    });
    this.map = map;

    await new Promise<void>(resolve => map.on('load', () => resolve()));
    if (this.destroyed) return;
    this.loaded = true;
    // Le conteneur peut être devenu visible après la création de la carte.
    map.resize();

    setTimeout(() => this.map?.easeTo({ pitch: 70, bearing: 30, duration: 3000 }), 1000);
    map.addControl(new mapboxgl.NavigationControl(), 'bottom-right');
    (map as any)['dragPan']._inertiaOptions = { maxSpeed: 400, linearity: 0.1, deceleration: 3500 };
    (map as any)['scrollZoom'].setWheelZoomRate(1 / 400);
    (map as any)['scrollZoom'].setZoomRate(1 / 100);

    // Les trous d'abord : ils fixent le repère commun aux deux scènes.
    await this.setBlasts(this.pendingIds);
    await this.loadSiteTerrain();
  }

  /** Affiche les trous et terrains des tirs donnés ; retire ceux qui ne le sont plus. */
  async setBlasts(blastIds: number[]): Promise<void> {
    this.pendingIds = blastIds;
    if (!this.map || !this.loaded || this.destroyed) return;

    const key = [...blastIds].sort((a, b) => a - b).join(',');
    if (key === this.lastKey && this.map.getLayer(HOLES_LAYER_ID)) return;

    const generation = ++this.generation;
    const stale = () => this.destroyed || generation !== this.generation;

    this.three ??= await import('three');
    if (stale()) return;
    const THREE = this.three;
    this.deps.onLoadingChange(true);

    try {
      const missing = blastIds.filter(id => !this.holesCache.has(id));
      if (missing.length > 0) {
        try {
          const blasts = await this.deps.holeService.GetBlastsHoles3D(missing);
          if (stale()) return;
          blasts.forEach(blast => { if (blast?.Holes) this.holesCache.set(blast.tirId, blast.Holes); });
        } catch {
          if (stale()) return;
        }
      }

      const terrainFetch = this.fetchMissingTerrains(blastIds);

      const selected = new Set(blastIds);
      this.removeBlasts([...this.holeMeshes.keys()].filter(id => !selected.has(id)));

      const toBuild: { blastId: number; holes: any[] }[] = [];
      for (const id of blastIds) {
        if (this.holeMeshes.has(id)) continue;
        const holes = this.holesCache.get(id);
        if (holes) toBuild.push({ blastId: id, holes });
      }

      if (!this.scene) {
        const first = toBuild.flatMap(b => b.holes).find(h => h.PositionPlannedXYZ && this.isNearSite(h));
        if (!first) return;
        this.ref = { x: first.PositionPlannedXYZ.x, y: first.PositionPlannedXYZ.y, z: first.PositionPlannedXYZ.z };
        this.scene = new THREE.Scene();
        this.addCustomLayer(HOLES_LAYER_ID, this.scene, this.ref, THREE);
      }

      const ref = this.ref!;
      const scene = this.scene;
      this.shared ??= {
        top: new THREE.MeshBasicMaterial({ color: 0xff0000 }),
        bottom: new THREE.MeshBasicMaterial({ color: 0x0000ff }),
        line: new THREE.LineBasicMaterial({ color: 0xffffff }),
        sphere: new THREE.SphereGeometry(0.3, 12, 12),
      };
      const shared = this.shared;

      for (const { blastId, holes } of toBuild) {
        const meshes: any[] = [];
        const top: number[] = [];
        const bottom: number[] = [];
        const lines: number[] = [];

        for (const hole of holes) {
          if (!hole.PositionPlannedXYZ || !hole.Geometry || !this.isNearSite(hole)) continue;
          const status = hole.Status?.toUpperCase();
          const x = hole.PositionPlannedXYZ.x - ref.x;
          const y = hole.PositionPlannedXYZ.y - ref.y;
          const z = hole.PositionPlannedXYZ.z - ref.z;
          top.push(x, y, z);

          if (status !== 'COLLAPSED' && status !== 'MISSING') {
            // Angle et azimut sont en radians.
            const angle = hole.Geometry.angle || 0;
            const azimuth = hole.Geometry.azimuth || 0;
            const length = hole.Geometry.length || 10;
            const dx = length * Math.sin(angle) * Math.sin(azimuth);
            const dy = length * Math.sin(angle) * Math.cos(azimuth);
            const dz = -length * Math.cos(angle);
            bottom.push(x + dx, y + dy, z + dz);
            lines.push(x, y, z, x + dx, y + dy, z + dz);
          }
        }

        const addSpheres = (positions: number[], material: any) => {
          const count = positions.length / 3;
          if (count === 0) return;
          const instanced = new THREE.InstancedMesh(shared.sphere, material, count);
          const matrix = new THREE.Matrix4();
          for (let i = 0; i < count; i++) {
            matrix.setPosition(positions[i * 3], positions[i * 3 + 1], positions[i * 3 + 2]);
            instanced.setMatrixAt(i, matrix);
          }
          instanced.instanceMatrix.needsUpdate = true;
          instanced.frustumCulled = false;
          scene.add(instanced);
          meshes.push(instanced);
        };
        addSpheres(top, shared.top);
        addSpheres(bottom, shared.bottom);

        if (lines.length > 0) {
          const geometry = new THREE.BufferGeometry();
          geometry.setAttribute('position', new THREE.Float32BufferAttribute(lines, 3));
          const segments = new THREE.LineSegments(geometry, shared.line);
          segments.frustumCulled = false;
          scene.add(segments);
          meshes.push(segments);
        }
        this.holeMeshes.set(blastId, meshes);
      }
      this.map?.triggerRepaint();

      await terrainFetch;
      if (stale()) return;

      const offset = new THREE.Vector3(ref.x, ref.y, ref.z);
      for (const blastId of blastIds.filter(id => !this.terrainGroups.has(id))) {
        const buffer = this.terrainCache.get(blastId);
        if (!buffer) continue;
        try {
          const data = await this.deps.terrainLoader.loadTerrainFromBinary(buffer.slice(0), offset);
          if (stale()) return;
          data.terrains.traverse((child: any) => {
            if (!child.isMesh) return;
            child.frustumCulled = false;
            const toBasic = (old: MeshPhongMaterial) => {
              const material = new THREE.MeshBasicMaterial({
                side: THREE.DoubleSide,
                transparent: true,
                opacity: TERRAIN_OPACITY,
                depthWrite: TERRAIN_OPACITY >= 1,
              });
              if (old.map) material.map = old.map;
              if (old.vertexColors) material.vertexColors = true;
              if (!old.map && !old.vertexColors) material.color = old.color || new THREE.Color(0x8b7355);
              return material;
            };
            // Terrain multi-matériaux : un matériau par groupe de faces, chacun converti.
            child.material = Array.isArray(child.material) ? child.material.map(toBasic) : toBasic(child.material);
          });
          scene.add(data.terrains);
          this.terrainGroups.set(blastId, data.terrains);
          this.map?.triggerRepaint();
        } catch {
          // Pas de terrain pour ce tir.
        }
        // Le décodage est synchrone et lourd : on laisse le navigateur peindre entre deux terrains.
        await new Promise<void>(resolve => setTimeout(resolve, 0));
        if (stale()) return;
      }

      this.lastKey = key;
    } finally {
      if (generation === this.generation) this.deps.onLoadingChange(false);
    }
  }

  destroy(): void {
    this.destroyed = true;
    this.generation++;
    if (this.scene) {
      const disposed = new Set<any>();
      this.scene.traverse((child: any) => {
        if (child.isInstancedMesh) child.dispose();
        if (child.geometry && !disposed.has(child.geometry)) {
          disposed.add(child.geometry);
          child.geometry.dispose();
        }
        const materials = Array.isArray(child.material) ? child.material : [child.material];
        materials.forEach((material: any) => {
          if (!material || disposed.has(material)) return;
          disposed.add(material);
          material.map?.dispose();
          material.dispose();
        });
      });
    }
    this.scene = null;
    this.shared = null;
    this.holeMeshes.clear();
    this.terrainGroups.clear();
    this.holesCache.clear();
    this.terrainCache.clear();
    this.map?.remove();
    this.map = null;
    this.loaded = false;
    this.deps.onLoadingChange(false);
  }

  private async loadSiteTerrain(): Promise<void> {
    const { site, terrainLoader, http } = this.deps;
    if (!site.siteId || !this.map || this.siteTerrainRequested) return;
    this.siteTerrainRequested = true;
    this.deps.onLoadingChange(true);

    try {
      let buffer: ArrayBuffer | null = null;
      try {
        const data = await firstValueFrom(http.getSiteTerrainBinary(site.siteId));
        if (data && data.byteLength > 0) buffer = data;
      } catch {
        // Pas de terrain sur le backend.
      }
      if (!buffer || this.destroyed || !this.map) return;

      this.three ??= await import('three');
      const THREE = this.three;
      const data = await terrainLoader.loadTerrainFromBinary(buffer);
      if (!data.terrains || this.destroyed || !this.map) return;

      // Même ancrage que les trous quand ils existent : l'exagération verticale
      // s'applique autour de l'altitude d'ancrage, deux ancrages feraient glisser les scènes.
      let offset = data.offset || { x: 0, y: 0, z: 0 };
      if (this.ref) offset = this.ref;
      const [lng, lat] = this.toWgs84(offset.x, offset.y);

      data.terrains.traverse((child: any) => {
        if (!child.isMesh) return;
        const positions = child.geometry.attributes.position;
        for (let i = 0; i < positions.count; i++) {
          positions.setXYZ(i, positions.getX(i) - offset.x, positions.getY(i) - offset.y, positions.getZ(i) - offset.z);
        }
        positions.needsUpdate = true;
        child.geometry.computeVertexNormals();
        child.material = new THREE.MeshBasicMaterial({
          vertexColors: true,
          side: THREE.DoubleSide,
          transparent: true,
          opacity: TERRAIN_OPACITY,
        });
      });

      const scene = new THREE.Scene();
      scene.add(data.terrains);
      this.addCustomLayer(SITE_TERRAIN_LAYER_ID, scene, { x: lng, y: lat, z: offset.z }, THREE, true);
      try { this.map.moveLayer(SITE_TERRAIN_LAYER_ID); } catch { /* calque absent */ }
    } catch {
      // Le terrain du site est facultatif : la 3D s'affiche sans.
    } finally {
      this.deps.onLoadingChange(false);
    }
  }

  /**
   * Branche une scène Three.js sur Mapbox. `anchor` est en coordonnées du site,
   * ou en lng/lat quand `isWgs84` est vrai.
   */
  private addCustomLayer(id: string, scene: any, anchor: { x: number; y: number; z: number }, THREE: any, isWgs84 = false): void {
    const map = this.map;
    if (!map) return;

    const [lng, lat] = isWgs84 ? [anchor.x, anchor.y] : this.toWgs84(anchor.x, anchor.y);
    const mercator = mapboxgl.MercatorCoordinate.fromLngLat([lng, lat], anchor.z);
    const scale = mercator.meterInMercatorCoordinateUnits();
    const camera = new THREE.Camera();
    let renderer: any;

    map.addLayer({
      id,
      type: 'custom',
      renderingMode: '3d',
      onAdd(_map: any, gl: WebGLRenderingContext) {
        renderer = new THREE.WebGLRenderer({ canvas: map.getCanvas(), context: gl, antialias: true });
        renderer.autoClear = false;
      },
      render(_gl: WebGLRenderingContext, matrix: number[]) {
        const m = new THREE.Matrix4().fromArray(matrix);
        const l = new THREE.Matrix4()
          .makeTranslation(mercator.x, mercator.y, mercator.z)
          .scale(new THREE.Vector3(scale, -scale, scale * TERRAIN_EXAGGERATION));
        camera.projectionMatrix = m.multiply(l);
        renderer.resetState();
        renderer.render(scene, camera);
        map.triggerRepaint();
      },
    } as mapboxgl.CustomLayerInterface);
    map.triggerRepaint();
  }

  private removeBlasts(blastIds: number[]): void {
    if (!blastIds.length || !this.scene) return;
    for (const id of blastIds) {
      this.holeMeshes.get(id)?.forEach(mesh => {
        this.scene.remove(mesh);
        this.disposeObject(mesh);
      });
      this.holeMeshes.delete(id);

      const group = this.terrainGroups.get(id);
      if (group) {
        this.scene.remove(group);
        group.traverse((child: any) => this.disposeObject(child));
        this.terrainGroups.delete(id);
      }
    }
    this.map?.triggerRepaint();
  }

  /** Libère un objet Three.js sans toucher aux ressources partagées entre tirs. */
  private disposeObject(object: any): void {
    const shared = this.shared;
    const isShared = (resource: any) => !!resource && !!shared && (
      resource === shared.sphere || resource === shared.top || resource === shared.bottom || resource === shared.line
    );
    if (object.isInstancedMesh) object.dispose();
    if (!isShared(object.geometry)) object.geometry?.dispose();
    const materials = Array.isArray(object.material) ? object.material : [object.material];
    materials.forEach((material: any) => {
      if (!material || isShared(material)) return;
      material.map?.dispose();
      material.dispose();
    });
  }

  private async fetchMissingTerrains(blastIds: number[]): Promise<void> {
    const missing = blastIds.filter(id => !this.terrainCache.has(id));
    if (!missing.length) return;

    // Le serveur refuse un lot plus large que son plafond : au-delà, le lot entier serait perdu.
    const chunks: number[][] = [];
    for (let i = 0; i < missing.length; i += MAX_TERRAINS_PER_REQUEST) chunks.push(missing.slice(i, i + MAX_TERRAINS_PER_REQUEST));

    const containers = await Promise.all(
      chunks.map(chunk => firstValueFrom(this.deps.http.getTerrainsBinary(chunk)).catch(() => null)),
    );
    containers.forEach(container => {
      if (container && container.byteLength > 0) this.unpackTerrains(container);
    });
  }

  /** Conteneur binaire : pour chaque terrain, id du tir (int32 LE), longueur (int32 LE), octets. */
  private unpackTerrains(container: ArrayBuffer): void {
    const view = new DataView(container);
    let offset = 0;
    while (offset + TERRAIN_HEADER_SIZE <= container.byteLength) {
      const blastId = view.getInt32(offset, true);
      const length = view.getInt32(offset + 4, true);
      offset += TERRAIN_HEADER_SIZE;
      if (length <= 0 || offset + length > container.byteLength) break;
      this.terrainCache.set(blastId, container.slice(offset, offset + length));
      offset += length;
    }
  }

  private toWgs84(x: number, y: number): [number, number] {
    const epsg = this.deps.site.epsg ? 'EPSG:' + this.deps.site.epsg : null;
    if (!epsg) return [x, y];
    const [lng, lat] = proj4(epsg, 'EPSG:4326', [x, y]);
    return [lng, lat];
  }

  /** Écarte les trous manifestement mal positionnés, qui étireraient la scène. */
  private isNearSite(hole: any): boolean {
    const { coordX, coordY } = this.deps.site;
    if (coordX == null || coordY == null) return true;
    const dx = hole.PositionPlannedXYZ.x - coordX;
    const dy = hole.PositionPlannedXYZ.y - coordY;
    return dx * dx + dy * dy <= MAX_DISTANCE_FROM_SITE * MAX_DISTANCE_FROM_SITE;
  }
}
